<template lang="pug">
    .event-list.container(v-if="section.eventCategories")
        .display-types.column(:class="['list'].includes(activeOrientation) ? ['is-offset-1', 'is-10'] : ''")
            .title-and-tools
                .pagination(v-if="activeOrientation !== 'list'")
                    nav.pagination.is-rounded(
                        role="navigation"
                        aria-label="pagination"
                    )
                        button.button.pagination-previous(
                            type="button"
                            tabindex="0"
                            @click="prev"
                        )
                            span.icon
                                i.fas.fa-arrow-left
                        button.pagination-previous(
                            type="button"
                            tabindex="0"
                            :class="{ 'is-active': isCurrent }"
                            :disabled="isCurrent"
                            @click="reset"
                        )
                            span.icon
                                i.fas.fa-angle-double-down
                        button.button.pagination-next(
                            type="button"
                            tabindex="0"
                            @click="next"
                        )
                            span.icon
                                i.fas.fa-arrow-right
                .date-title
                    h3(v-if="['week', 'month'].includes(activeOrientation)") {{ title }}
                .field.display-types.has-addons.is-justify-content-flex-end(v-if="! forcedOrientation && orientationOptions.length > 1")
                    p.control(v-if="orientationOptions.includes('list')")
                        button.button(
                            :class="{ 'is-active': activeOrientation === 'list' }"
                            @click="setPreferredOrientation('list')"
                        )
                            span.icon.is-small
                                i.fas.fa-th-list
                    p.control(v-if="orientationOptions.includes('month')")
                        button.button(
                            :class="{ 'is-active': activeOrientation === 'month' }"
                            @click="setPreferredOrientation('month')"
                        )
                            span.icon.is-small
                                i.fas.fa-calendar-alt
                    p.control(v-if="orientationOptions.includes('week')")
                        button.button(
                            :class="{ 'is-active': activeOrientation === 'week' }"
                            @click="setPreferredOrientation('week')"
                        )
                            span.icon.is-small
                                i.fas.fa-calendar-week
                    p.control(v-if="orientationOptions.includes('day')")
                        button.button(
                            :class="{ 'is-active': activeOrientation === 'day' }"
                            @click="setPreferredOrientation('day')"
                        )
                            span.icon.is-small
                                i.fas.fa-calendar-day
        .teaser-list(v-if="activeOrientation === 'list'")
            .column.is-offset-1.is-10
                TeaserList(:section="section" :per-page="perPage")
        .calendar-month.column(v-else-if="activeOrientation === 'month'")
            MonthSchedule(:section="section" :current="current")
        .calendar-week.column(v-else-if="activeOrientation === 'week'")
            WeekSchedule(:section="section" :current="current" @update="date => current = date")
        .calendar-day.column(v-else-if="activeOrientation === 'day'")
            DaySchedule(:section="section" :current="current" @update="date => current = date")
</template>

<script>

import _ from 'lodash'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { format, add, sub, isSameMonth, isSameWeek, isSameDay } from 'date-fns'
import { de } from 'date-fns/locale'

import store from '@/store'
import { formatDate, timestamp2date } from '@/filters';

import TeaserList from './Partials/TeaserList'
import MonthSchedule from './Partials/MonthSchedule'
import WeekSchedule from './Partials/WeekSchedule'
import DaySchedule from './Partials/DaySchedule'

export default {
    name: 'EventList',
    props: {
        s: {
            type: Number,
            required: true,
        },
        section: {
            type: Object,
            required: true,
        },
        perPage: {
            type: Number,
            default: 15,
        },
        defaultOrientation: {
            type: String,
            default: 'list',
            validator: val => ['list', 'month', 'week', 'day'].includes(val)
        },
        forcedOrientation: {
            type: String,
            validator: val => ['list', 'month', 'week', 'day'].includes(val)
        }
    },
    data: () => {
        return {
            fetching: false,
            current: new Date,
        }
    },
    components: {
        TeaserList,
        MonthSchedule,
        WeekSchedule,
        DaySchedule,
    },
    watch: {
        activeOrientation(value) {
            if (value === 'list') this.insertTeasers({ s: this.s, teasers: { loading: true } });
        }
    },
    computed: {
        settings() {
            return this.section.settings || []
        },
        orientationOptions() {
            const setting = this.settings.find(el => el.key === 'display-types');
            if (!setting || !setting.options || !setting.options.length) return ['list', 'month'];
            else return setting.options.map(el => el.key);
        },
        activeOrientation() {
            if (this.forcedOrientation) return this.forcedOrientation;
            let orientation = this.defaultOrientation;

            switch (this.preferredOrientation) {
                case 'list':
                    if (this.orientationOptions.includes('list')) orientation = this.preferredOrientation;
                    break;

                case 'month':
                    if (this.orientationOptions.includes('month')) orientation = this.preferredOrientation;
                    break;

                case 'week':
                    if (this.orientationOptions.includes('week')) orientation = this.preferredOrientation;
                    break;

                case 'day':
                    if (this.orientationOptions.includes('day')) orientation = this.preferredOrientation;
                    break;
            }

            if (! orientation || ! this.orientationOptions.includes(orientation)) {
                const orientations = { list: 'list', month: 'month', week: 'week', day: 'day' };
                orientation = orientations[this.orientationOptions[0]];
            }

            return orientation
        },
        title() {
            let title
            if (this.activeOrientation === 'week' && _.get(this.section, 'teasers.pagination.filters.length')) {
                const filter = this.section.teasers.pagination.filters.find(el => el.key === 'inclusive_timespan')
                if (this.preferredWeekPeriod === 7) {
                    if (filter) title = `${format(timestamp2date(filter.from), 'I.')} Kalenderwoche ${format(timestamp2date(filter.from), 'yyyy')}`
                } else {
                    if (filter) title = `${format(timestamp2date(filter.from), 'I.')} KW`
                    // if (filter) title = `${format(timestamp2date(filter.from), 'MMM. yyyy')}`
                }
            } else if (this.activeOrientation === 'month' && _.get(this.section, 'teasers.pagination.filters.length')) {
                const filter = this.section.teasers.pagination.filters.find(el => el.key === 'inclusive_timespan')
                if (filter) title = `${format(timestamp2date(filter.from), 'MMMM yyyy', { locale: de })}`
            }

            return title
        },
        isCurrent() {
            let current = true
            switch (this.activeOrientation) {
                case 'month':
                    if (this.current) {
                        current = isSameMonth(new Date, this.current)
                    }
                    break;

                case 'week':
                    if (this.current) {
                        if (this.preferredWeekPeriod === 7) current = isSameWeek(new Date, this.current, { weekStartsOn: 1 })
                        else if (this.preferredWeekPeriod === 1) current = isSameDay(new Date, this.current)
                        // TODO: handle others by checking for overlapping
                    }
                    break;

                case 'day':
                    if (this.current) {
                        current = isSameDay(new Date, this.current)
                    }
                    break;
            }
            return current
        },
        ...mapGetters({
            sections: 'currentPage/sections',
            preferredOrientation: 'event/preferredListOrientation',
            preferredWeekPeriod: 'event/preferredWeekPeriod',
            cms: 'CMS',
        })
    },
    methods: {
        next() {
            switch (this.activeOrientation) {
                case 'month':
                    if (this.current) this.current = add(this.current, { months: 1 })
                    break;

                case 'week':
                    if (this.current) this.current = add(this.current, { days: this.preferredWeekPeriod })
                    break;

                case 'day':
                    if (this.current) this.current = add(this.current, { days: 1 })
                    break;
            }
        },
        prev() {
            switch (this.activeOrientation) {
                case 'month':
                    if (this.current) this.current = sub(this.current, { months: 1 })
                    break;

                case 'week':
                    if (this.current) this.current = sub(this.current, { days: this.preferredWeekPeriod })
                    break;

                case 'day':
                    if (this.current) this.current = sub(this.current, { days: 1 })
                    break;
            }
        },
        reset() {
            switch (this.activeOrientation) {
                case 'month':
                case 'week':
                case 'day':
                    this.current = new Date
                    break;
            }
        },
        ...mapMutations({
            setPreferredOrientation: 'event/setPreferredListOrientation',
            // updateTeaserFilter: 'currentPage/updateTeaserFilter',
            insertTeasers: 'currentPage/insertTeasers',
        }),
    },
}
</script>

<style lang="sass" scoped>

@import "~bulma/sass/utilities/mixins";

.title-and-tools
    display: flex
    justify-content: space-between
    align-items: center
.pagination.is-rounded
    .pagination-previous,
    .pagination-next
        padding-left: 0.3em
        padding-right: 0.3em
.date-title
    h3
        font-size: 1rem
        +tablet
            font-size: 1.25rem

.display-types,
nav.pagination
    .pagination-previous,
    .button
        font-size: .8rem
        .icon
            width: 18px
        +tablet
            font-size: 1rem
            .icon
                width: 24px
</style>
